// RamsPreview — interactive "preview as client" modal. Lets a reviewer answer
// questions live so conditional show/require logic can be sanity-checked before
// publishing. This is a builder preview only, not the real client portal (Phase 3).
import { useState } from "react";
import { X, Info } from "lucide-react";

function ruleMatches(rule, answers) {
    const val = answers[rule.source_key];
    switch (rule.operator) {
        case "is_yes": return val === "Yes" || val === true;
        case "is_no": return val === "No" || val === false;
        case "not_empty": return val !== undefined && val !== null && val !== "";
        case "not_equals": return String(val ?? "") !== String(rule.value ?? "");
        case "in": return (rule.value || "").split(",").map((v) => v.trim()).includes(String(val ?? ""));
        default: return String(val ?? "") === String(rule.value ?? "");
    }
}
function isVisible(question, answers) {
    const showRules = (question.conditional_rules || []).filter((r) => r.action === "show");
    if (showRules.length === 0) return true;
    return showRules.every((r) => ruleMatches(r, answers));
}
function isRequired(question, answers) {
    if (question.required) return true;
    const reqRules = (question.conditional_rules || []).filter((r) => r.action === "require");
    return reqRules.length > 0 && reqRules.every((r) => ruleMatches(r, answers));
}

export default function RamsPreview({ sections, onClose }) {
    const [answers, setAnswers] = useState({});
    const set = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="rams-preview-modal">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md h-[85vh] flex flex-col overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between bg-[#F5F3F0]">
                    <div>
                        <h3 className="text-base font-bold text-[#1F1F1F]">Preview as client</h3>
                        <p className="text-[11px] text-[#9A968F] flex items-center gap-1"><Info size={11} />Answer questions to test conditional logic</p>
                    </div>
                    <button data-testid="rams-preview-close" onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-5 space-y-6">
                    {sections.map((sec) => {
                        const visibleQs = (sec.questions || []).filter((q) => isVisible(q, answers));
                        if (visibleQs.length === 0) return null;
                        return (
                            <div key={sec.id}>
                                <h4 className="text-sm font-bold text-[#1F1F1F] mb-2">{sec.title}</h4>
                                <div className="space-y-3">
                                    {visibleQs.map((q) => (
                                        <PreviewQuestion key={q.id} question={q} required={isRequired(q, answers)}
                                            value={answers[q.field_key]} onChange={(v) => set(q.field_key, v)} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function PreviewQuestion({ question, required, value, onChange }) {
    const label = <label className="block text-[12px] font-semibold text-[#1F1F1F] mb-1">{question.label}{required && <span className="text-red-600 ml-1">*</span>}</label>;
    const cls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm";

    if (question.question_type === "richtext_guidance") {
        return <div className="p-3 rounded-lg bg-[#FBEFE5] text-[12px] text-[#6F6A64]">{question.help_text || question.label}</div>;
    }
    if (question.question_type === "yes_no") {
        return <div>{label}
            <div className="flex gap-2">
                {["Yes", "No"].map((opt) => (
                    <button key={opt} onClick={() => onChange(opt)}
                        className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border ${value === opt ? "border-[var(--brand-accent,_#C75A1B)] bg-[#FBEFE5]" : "border-[#E5E0DA]"}`}>{opt}</button>
                ))}
            </div>
        </div>;
    }
    if (question.question_type === "select") {
        return <div>{label}
            <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls}>
                <option value="">Select…</option>
                {(question.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>;
    }
    if (question.question_type === "textarea") {
        return <div>{label}<textarea rows={2} value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls + " resize-none h-auto py-2"} /></div>;
    }
    if (["file", "photo", "drawing"].includes(question.question_type)) {
        return <div>{label}<div className="text-[11px] text-[#9A968F] italic border border-dashed border-[#E5E0DA] rounded-lg p-2">Upload — {question.question_type}</div></div>;
    }
    if (question.question_type === "declaration_signature") {
        return <div>{label}<div className="text-[11px] text-[#9A968F] italic border border-dashed border-[#E5E0DA] rounded-lg p-2 h-16 flex items-center justify-center">Signature pad</div></div>;
    }
    return <div>{label}<input value={value || ""} onChange={(e) => onChange(e.target.value)} className={cls} /></div>;
}
